// Веб-вклад модуля `courses` — Обучение (курсы, темы, воркспейс).
//
// Приоритетный раздел: янтарный счётчик активных курсов и вложенный быстрый
// список последних курсов под пунктом. `/study-plan/*` — легаси-адреса
// (редиректят сюда) плюс живой `/study-plan/templates`: раздел обязан
// подсвечиваться и на них.
//
// Собственных плиток воркспейса у курса нет и быть не может: курс — каркас, в
// котором живут форматы, а не седьмой формат.

import { SECTION_CATEGORY, SECTION_ICONS } from "@/components/navigation/section-icons";
import type { WebModuleManifest } from "./_contract";

/**
 * Активный курс = тот, к которому пользователь реально возвращается: не удалён,
 * не в архиве, не на паузе, не завершён и не пройден до конца.
 *
 * Определение живёт здесь, а не в хуке сайдбара: и счётчик пункта, и быстрый
 * список последних курсов обязаны понимать «активный» одинаково — разъехавшись,
 * они дают бейдж «3» над списком из двух строк.
 *
 * ⚠️ Одного `status` мало. `study_plans.status` в `completed` НИКТО не
 * переводит автоматически: каскад `recomputeProgressForActivity` обновляет
 * только счётчики прогресса плана, а статус меняет лишь ручное действие
 * пользователя (архив/пауза). Поэтому курс, у которого пройдены все темы,
 * навсегда оставался бы `active` и продолжал висеть в янтарном счётчике —
 * ровно то, на что жалуется пользователь («16» при половине пройденных).
 *
 * Завершённость считаем по темам, а не по `progress_pct`: процент плана —
 * это округлённое среднее по темам, и он выдаёт 100 при 99.6 (одна тема
 * недокрыта). `completed_subsections >= total_subsections` — точный признак.
 * `total_subsections = 0` (программа ещё не построена) завершённостью НЕ
 * считается, иначе только что созданный курс сразу выпадал бы из счётчика.
 *
 * `paused` тоже вне счёта (решение владельца 2026-08-20): пауза — это явное
 * «сейчас я его не учу», и держать такой курс в янтарном бейдже «сколько на
 * мне висит» значит врать о нагрузке. Обратно в счётчик он возвращается сам —
 * `resumeStudyPlan` переводит статус в `active`.
 */
export const ACTIVE_COURSES_PREDICATE = `
	p.user_id = ?
	  AND p.deleted_at IS NULL
	  AND (p.status IS NULL OR p.status NOT IN ('archived', 'completed', 'paused'))
	  AND NOT (
	    coalesce(p.total_subsections, 0) > 0
	      AND coalesce(p.completed_subsections, 0) >= coalesce(p.total_subsections, 0)
	  )
`;

export const coursesWebManifest = {
	id: "courses",
	icon: SECTION_ICONS.course,
	labelKey: "nav.courses",
	category: SECTION_CATEGORY.course,
	routes: ["/courses", "/study-plan"],

	// Баннер объясняет МЕХАНИКУ: курс не заводится вручную по разделам, а
	// собирается ИИ из загруженных источников и сам раскладывается по
	// календарю. Вкладка справки — «Как собрать курс».
	intro: {
		titleKey: "courses:intro.title",
		bodyKey: "courses:intro.body",
		bulletKeys: ["courses:intro.bullets.0", "courses:intro.bullets.1", "courses:intro.bullets.2"],
	},

	nav: [
		{
			key: "courses",
			to: "/courses",
			icon: SECTION_ICONS.course,
			labelKey: "nav.courses",
			activePrefixes: ["/study-plan"],
			badgeTone: "warning",
			order: 20,
			badge: {
				kind: "powersync",
				sql: `SELECT count(*) FROM study_plans p WHERE ${ACTIVE_COURSES_PREDICATE}`,
			},
		},
	],
} satisfies WebModuleManifest;
